/**
 * WorldCatalog — worlds read view (U2.5). 6 seeded worlds. Resolves
 * realmId → realm name so each world shows the realm it belongs to.
 * Reuses the shared `.race-*` catalog layout.
 */

import { useEffect, useMemo, useState } from "react";

import { fetchWorlds, fetchRealms, type WorldDTO, type RealmDTO } from "../api.js";

type LoadState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; worlds: WorldDTO[]; realmNames: Map<string, string> };

export function WorldCatalog() {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchWorlds(), fetchRealms()])
      .then(([worlds, realms]) => {
        if (cancelled) return;
        const realmNames = new Map(realms.map((r: RealmDTO) => [r.id, r.name]));
        setState({ kind: "ready", worlds, realmNames });
        if (worlds.length > 0) setSelectedId(worlds[0].id);
      })
      .catch((err: Error) => {
        if (!cancelled) setState({ kind: "error", message: err.message });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selected = useMemo(() => {
    if (state.kind !== "ready") return null;
    return state.worlds.find((w) => w.id === selectedId) ?? null;
  }, [state, selectedId]);

  if (state.kind === "loading") return <div className="placeholder">Loading worlds…</div>;
  if (state.kind === "error") {
    return (
      <div className="placeholder" style={{ color: "#ef4444" }}>
        <strong>Failed to load worlds:</strong> {state.message}
      </div>
    );
  }

  return (
    <div className="race-catalog">
      <aside className="race-list">
        <div className="race-list-header">
          <h2>Worlds</h2>
          <span className="count">{state.worlds.length}</span>
        </div>
        <ul>
          {state.worlds.map((w) => (
            <li key={w.id}>
              <button
                className={`race-list-item ${w.id === selectedId ? "active" : ""}`}
                onClick={() => setSelectedId(w.id)}
              >
                <span className="name">{w.name}</span>
                {w.type && <span className="lifespan">{w.type}</span>}
              </button>
            </li>
          ))}
        </ul>
      </aside>
      <section className="race-detail">
        {selected ? (
          <WorldDetail world={selected} realmNames={state.realmNames} />
        ) : (
          <div className="placeholder">Pick a world.</div>
        )}
      </section>
    </div>
  );
}

function WorldDetail({
  world,
  realmNames,
}: {
  world: WorldDTO;
  realmNames: Map<string, string>;
}) {
  const realm = realmNames.get(world.realmId) ?? "Unknown realm";
  return (
    <article>
      <header className="detail-header">
        <h2>{world.name}</h2>
        {world.type && <span className="category">{world.type}</span>}
      </header>

      <dl className="detail-grid">
        <dt>Realm</dt>
        <dd>{realm}</dd>
        <dt>Atmosphere</dt>
        <dd>{world.atmosphere ? world.atmosphere : <span className="empty">—</span>}</dd>
      </dl>

      {world.description.length > 0 && (
        <section className="description">
          <h3>Description</h3>
          {world.description.map((entry, i) => (
            <p key={i} className={`tiered tier-${entry.tier}`}>
              {entry.tier === "tier_2" && <span className="tier-badge">tier 2</span>}
              {entry.text}
            </p>
          ))}
        </section>
      )}
    </article>
  );
}
